import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'
import assign from 'lodash/assign'
import pick from 'lodash/pick'

import { expertApi } from '@atheneum/api'

import AccountNumberTooltip from './AccountNumberTooltip'
import BankCodeTooltip from './BankCodeTooltip'
import { getValidationSchema } from './ExpertPaymentDetailsFormValidationSchema'
import VatTooltip from './VatTooltip'
import Button from '@components/Button'
import { StatusLabel } from '@components/StatusLabel'
import FormError from '@components/FormError'
import FormLoader from '@components/FormLoader'
import { Autocomplete, Dropdown } from '@components/Form/Autocomplete'
import Checkbox from '@components/Form/Checkbox'
import Input from '@components/Form/Input'
import TextArea from '@components/Form/TextArea'

class ExpertPaymentDetailsForm extends Component {
  static propTypes = {
    modalStore: PropTypes.object,
    paymentData: PropTypes.object,
    staticData: PropTypes.object,
    paymentDetailsStore: PropTypes.object
  }

  state = {
    error: null
  }

  renderDirectTransferOrDontaionOther = form => {
    const { staticData } = this.props
    const {
      DIRECT_TRANSFER,
      DONATION_OTHER
    } = staticData.CONST.PAYMENT_TRANSFER_METHODS
    const relevantTransferMethods = [DIRECT_TRANSFER, DONATION_OTHER]
    const { paymentTransferMethodId } = form.values

    if (!relevantTransferMethods.includes(paymentTransferMethodId)) return null

    return (
      <div className='m-t-lg'>
        <Field name='accountOwner' label='Account owner' component={Input} />
        <Field
          name='accountNumber'
          label='Account number/IBAN'
          tooltip={<AccountNumberTooltip />}
          component={Input}
        />
        <Field
          name='bankCode'
          label='SWIFT CODE/ROUTING NUMBER'
          tooltip={<BankCodeTooltip />}
          component={Input}
        />
        <Field name='bankName' label='Bank name' component={Input} />
        <Field
          name='bankAddress.address1'
          label='Bank street address'
          component={Input}
        />
        <div className='expert-form__input-group'>
          <label className='expert-form__label'>Bank city / ZIP code</label>
          <div className='expert-form__input-wrapper expert-form__input-wrapper--two_one_children'>
            <Field name='bankAddress.city' component={Input} />
            <Field name='bankAddress.postalCode' component={Input} />
          </div>
        </div>
        <Field name='bankAddress.state' label='Bank state' component={Input} />
        <Field
          valueIsId
          showResultsOnEmptyInput
          name='bankAddress.countryId'
          label='Bank country'
          itemToString={item => item && item.name}
          onChange={country => {
            country
              ? form.setFieldValue('bankAddress.countryId', country.id)
              : form.setFieldValue('bankAddress.countryId', null)
          }}
          options={staticData.countries}
          component={Autocomplete}
        />
      </div>
    )
  }

  submitForm = (data, actions) => {
    const { paymentData, staticData } = this.props
    this.savePaymentDetailsData(data, paymentData, staticData)
      .then(() => {
        const { modalStore, paymentDetailsStore } = this.props
        const expertId = paymentData.expertId
        paymentDetailsStore.get(expertId)
        modalStore.close()
      })
      .catch(({ message }) => {
        this.setState({ error: message })
        actions.setSubmitting(false)
      })
  }

  savePaymentDetailsData (data, paymentDetails, staticData) {
    const { PRIVATE } = staticData.CONST.PAYMENT_SERVICE_TYPES
    const {
      PAYPAL,
      DONATION_CHILDREN_FOR_A_BETTER_WORLD
    } = staticData.CONST.PAYMENT_TRANSFER_METHODS

    const expertsAddress = data.expert.address
    const getVatClass = staticData.vatClasses.find(
      vatClass => vatClass.id === data.vatClassId
    )
    const vatClass = getVatClass ? getVatClass.value : 0

    if (data.paymentServiceTypeId === PRIVATE) {
      data.companyName = null
      data.vatNumber = null
    }
    if (
      data.paymentTransferMethodId === PAYPAL ||
      data.paymentTransferMethodId === DONATION_CHILDREN_FOR_A_BETTER_WORLD
    ) {
      data.accountOwner = null
      data.accountNumber = null
      data.bankCode = null
      data.bankName = null
    }

    const paymentDetailsData = {
      ...data,
      expertsAddress: expertsAddress,
      vatClass: vatClass
    }
    return expertApi.savePaymentDetails(paymentDetails.expertId, paymentDetailsData)
  }

  render () {
    const { paymentData, modalStore, staticData } = this.props

    const { BUSINESS } = staticData.CONST.PAYMENT_SERVICE_TYPES
    const { PAYPAL } = staticData.CONST.PAYMENT_TRANSFER_METHODS
    const { GERMANY } = staticData.CONST.COUNTRIES

    return (
      <div>
        <Formik
          enableReinitialize
          initialValues={initializeForm({ paymentData, staticData })}
          validationSchema={getValidationSchema(staticData)}
          onSubmit={this.submitForm}
          render={form => (
            <div className='expert-form'>
              <FormLoader loading={form.isSubmitting} />
              <h1 className='expert-form__title'>
                Payment details
                <StatusLabel
                  statusId={paymentData.expertPaymentDetailStatusId}
                  statusesType='expertPaymentDetailStatuses'
                />
              </h1>
              <FormError error={this.state.error} />
              <Form>
                <div className='expert-form__section'>
                  <div className='expert-form__input-group'>
                    <label className='expert-form__label'>
                      Payment service type
                    </label>
                    <div className='expert-form__input-wrapper expert-form__input-wrapper--two_one_children'>
                      <Field
                        name='paymentServiceTypeId'
                        placeholder='Select payment service type'
                        itemToString={item => item && item.name}
                        options={staticData.paymentServiceTypes}
                        component={Dropdown}
                      />
                      <Field
                        name='invoiceNeeded'
                        label='Invoice needed'
                        tooltip={
                          'Check this option if expert requires an invoice sent to their email upon payment'
                        }
                        component={Checkbox}
                      />
                    </div>
                  </div>
                  {form.values.paymentServiceTypeId === BUSINESS && (
                    <div className='m-t-lg'>
                      <Field
                        name='companyName'
                        label='Company name'
                        component={Input}
                      />
                      <Field
                        name='vatNumber'
                        label='VAT number'
                        component={Input}
                      />
                    </div>
                  )}
                  {form.values.invoiceNeeded && (
                    <div className='m-t-lg'>
                      <Field
                        short
                        name='vatClassId'
                        label='VAT class'
                        tooltip={<VatTooltip />}
                        placeholder=' '
                        itemToString={item => item && item.name}
                        options={staticData.vatClasses.sort(
                          (a, b) => a.value - b.value
                        )}
                        component={Dropdown}
                        disabled={
                          form.values.expert.address.countryId !== GERMANY
                        }
                      />
                    </div>
                  )}
                  <div className='m-t-lg'>
                    <Field
                      name='expert.address.address1'
                      label='Address'
                      component={Input}
                    />
                    <div className='expert-form__input-group'>
                      <label className='expert-form__label'>
                        City / Postal code
                      </label>
                      <div className='expert-form__input-wrapper expert-form__input-wrapper--two_one_children'>
                        <Field name='expert.address.city' component={Input} />
                        <Field
                          name='expert.address.postalCode'
                          component={Input}
                        />
                      </div>
                    </div>
                    <Field
                      name='expert.address.state'
                      label='State / province'
                      component={Input}
                    />
                    <Field
                      valueIsId
                      showResultsOnEmptyInput
                      name='expert.address.countryId'
                      label='Country'
                      itemToString={item => item && item.name}
                      onChange={country => {
                        country
                          ? form.setFieldValue(
                              'expert.address.countryId',
                              country.id
                            )
                          : form.setFieldValue('expert.address.countryId', null)
                      }}
                      options={staticData.countries}
                      component={Autocomplete}
                    />
                  </div>
                </div>
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>Payment method</h2>
                  <Field
                    name='paymentTransferMethodId'
                    label='Payment transfer method'
                    placeholder='Select payment transfer method'
                    itemToString={item => item && item.name}
                    options={staticData.paymentTransferMethods}
                    component={Dropdown}
                  />
                  {this.renderDirectTransferOrDontaionOther(form)}
                  {form.values.paymentTransferMethodId === PAYPAL && (
                    <Field
                      name='payPalEmail'
                      label='PayPal email'
                      component={Input}
                    />
                  )}
                  <div className='m-t-lg'>
                    <Field
                      name='comment'
                      label='Comments'
                      component={TextArea}
                    />
                  </div>
                </div>
                <div className='expert-form__section p-b-0'>
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}
                  >
                    Save
                  </Button>
                  <Button onClick={modalStore.close}>Cancel</Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

ExpertPaymentDetailsForm.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  shouldCloseOnOverlayClick: false
}

export default withRouter(
  inject(
    'modalStore',
    'staticData',
    'paymentDetailsStore'
  )(observer(ExpertPaymentDetailsForm))
)

function initializeForm ({ paymentData, staticData }) {
  const { DIRECT_TRANSFER } = staticData.CONST.PAYMENT_TRANSFER_METHODS

  return assign(
    pick(toJS(paymentData), [
      'paymentServiceTypeId',
      'invoiceNeeded',
      'companyName',
      'vatNumber',
      'accountOwner',
      'accountNumber',
      'bankCode',
      'bankName',
      'payPalEmail',
      'comment'
    ]),
    {
      vatClassId: staticData.CONST.VAT_CLASSES[paymentData.vatClass],
      expert: {
        address: getExpertAddress(paymentData)
      },
      paymentTransferMethodId:
        paymentData.paymentTransferMethodId || DIRECT_TRANSFER,
      bankAddress: getBankAddress(paymentData)
    }
  )
}

function getExpertAddress (paymentData) {
  const expertAddress = paymentData.expertsAddress || paymentData.expert.address
  return {
    address1: expertAddress.address1,
    city: expertAddress.city,
    postalCode: expertAddress.postalCode,
    state: expertAddress.state,
    countryId: expertAddress.countryId
  }
}

function getBankAddress (paymentData) {
  const bankAddress = paymentData.bankAddress
    ? paymentData.bankAddress
    : {
        address1: '',
        city: '',
        postalCode: '',
        state: '',
        countryId: null
      }
  return bankAddress
}
